import { useMutation, useSuspenseQuery } from '@apollo/client';
import { useState } from 'react';
import { Helmet } from 'react-helmet';

import { cacheUtils, useCompleteFragment } from '@eluve/apollo-client';
import {
  ConversationStarter,
  ConversationStarters,
  CustomChatInstructions,
  KnowledgeSources,
  UploadedFile,
} from '@eluve/blocks';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Content,
  HStack,
  Header,
  LoadingIndicator,
  NewButton,
  PageTitle,
  SidebarTrigger,
  VStack,
  useToast,
} from '@eluve/components';
import { DEFAULT_STARTING_MESSAGES } from '@eluve/feature-chat-with-patient-data';
import { graphql } from '@eluve/graphql.tada';
import { useAnalytics } from '@eluve/posthog-react';
import {
  useAssignedTenantIdFromParams,
  useUserIdFromSession,
} from '@eluve/session-helpers';

const chatSettingsFragment = graphql(`
  fragment ChatSettings on TenantUserChatSettings @_unmask {
    __typename
    tenantId
    userId
    customInstructions
    contextSources
    files
    conversationStarters
  }
`);

const getChatSettingsQuery = graphql(
  `
    query getChatSettings($tenantId: uuid!, $userId: uuid!) {
      tenantUserChatSettingsByPk(tenantId: $tenantId, userId: $userId) {
        ...ChatSettings
      }
    }
  `,
  [chatSettingsFragment],
);

// Upsert - creates new chat setting record if none exists
const upsertChatSettingsMutation = graphql(
  `
    mutation UpsertChatSettings(
      $customInstructions: String
      $contextSources: [String!]
      $files: jsonb
      $conversationStarters: jsonb
    ) {
      insertTenantUserChatSettingsOne(
        object: {
          customInstructions: $customInstructions
          contextSources: $contextSources
          files: $files
          conversationStarters: $conversationStarters
        }
        onConflict: {
          constraint: tenant_user_chat_settings_pkey
          updateColumns: [
            customInstructions
            contextSources
            files
            conversationStarters
          ]
        }
      ) {
        ...ChatSettings
      }
    }
  `,
  [chatSettingsFragment],
);

const updateContextSourcesMutation = graphql(
  `
    mutation UpdateContextSources(
      $tenantId: uuid!
      $userId: uuid!
      $contextSources: [String!]
    ) {
      updateTenantUserChatSettingsByPk(
        pkColumns: { tenantId: $tenantId, userId: $userId }
        _set: { contextSources: $contextSources }
      ) {
        ...ChatSettings
      }
    }
  `,
  [chatSettingsFragment],
);

const updateCustomInstructionsMutation = graphql(
  `
    mutation UpdateCustomInstructions(
      $tenantId: uuid!
      $userId: uuid!
      $customInstructions: String
    ) {
      updateTenantUserChatSettingsByPk(
        pkColumns: { tenantId: $tenantId, userId: $userId }
        _set: { customInstructions: $customInstructions }
      ) {
        ...ChatSettings
      }
    }
  `,
  [chatSettingsFragment],
);

const updateConversationStartersMutation = graphql(
  `
    mutation UpdateConversationStarters(
      $tenantId: uuid!
      $userId: uuid!
      $conversationStarters: jsonb!
    ) {
      updateTenantUserChatSettingsByPk(
        pkColumns: { tenantId: $tenantId, userId: $userId }
        _set: { conversationStarters: $conversationStarters }
      ) {
        ...ChatSettings
      }
    }
  `,
  [chatSettingsFragment],
);

const updateFilesMutation = graphql(
  `
    mutation UpdateFiles($tenantId: uuid!, $userId: uuid!, $files: jsonb!) {
      updateTenantUserChatSettingsByPk(
        pkColumns: { tenantId: $tenantId, userId: $userId }
        _set: { files: $files }
      ) {
        ...ChatSettings
      }
    }
  `,
  [chatSettingsFragment],
);

const deleteChatSettingsMutation = graphql(`
  mutation DeleteChatSettings($tenantId: uuid!, $userId: uuid!) {
    deleteTenantUserChatSettingsByPk(tenantId: $tenantId, userId: $userId) {
      __typename
      tenantId
      userId
    }
  }
`);

export const TenantUserChatSettingsPage = () => {
  const tenantId = useAssignedTenantIdFromParams();
  const userId = useUserIdFromSession();
  const analytics = useAnalytics('chat_settings');

  const [showResetDialog, setShowResetDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();

  useSuspenseQuery(getChatSettingsQuery, {
    variables: { tenantId, userId },
  });

  const settings = useCompleteFragment({
    fragment: chatSettingsFragment,
    key: { tenantId, userId },
    strict: false,
  });

  const customInstructions = settings?.customInstructions ?? '';
  const contextSources = settings?.contextSources ?? [];
  const conversationStarters = (settings?.conversationStarters ??
    DEFAULT_STARTING_MESSAGES) as ConversationStarter[];
  const files = (settings?.files ?? []) as UploadedFile[];

  const [upsertSettings] = useMutation(upsertChatSettingsMutation, {
    onCompleted: (data) => {
      if (data.insertTenantUserChatSettingsOne) {
        const created = data.insertTenantUserChatSettingsOne;
        analytics.capture('chat_settings_upserted', {
          tenantId,
          userId,
          customInstructions: created?.customInstructions,
          conversationStarters: created?.conversationStarters,
          contextSources: created?.contextSources,
          files: created?.files,
        });
      }
    },
  });

  const [updateCustomInstructions] = useMutation(
    updateCustomInstructionsMutation,
    {
      onCompleted: (data) => {
        if (data.updateTenantUserChatSettingsByPk) {
          const updated = data.updateTenantUserChatSettingsByPk;
          analytics.capture('chat_custom_instructions_update', {
            tenantId,
            userId,
            previous: customInstructions,
            updated: updated.customInstructions,
          });
        }
      },
      onError: () => toast.error('Failed to update custom instructions'),
    },
  );

  const [updateContextSources] = useMutation(updateContextSourcesMutation, {
    onCompleted: (data) => {
      if (data.updateTenantUserChatSettingsByPk) {
        const updated = data.updateTenantUserChatSettingsByPk;
        const previousSources = contextSources ?? [];
        const newSources = updated.contextSources ?? [];
        analytics.capture('chat_context_sources_update', {
          tenantId,
          userId,
          updated: newSources,
          added: newSources.filter((s) => !previousSources.includes(s)),
          removed: previousSources.filter((s) => !newSources.includes(s)),
        });
      }
    },
    onError: () => toast.error('Failed to update context sources'),
  });

  const [updateConversationStarters] = useMutation(
    updateConversationStartersMutation,
    {
      onCompleted: (data) => {
        if (data.updateTenantUserChatSettingsByPk) {
          const previous = conversationStarters ?? [];
          const updated =
            (data.updateTenantUserChatSettingsByPk
              .conversationStarters as ConversationStarter[]) ?? [];
          const prevIds = new Set(previous.map((s) => s.id));
          const newIds = new Set(updated.map((s) => s.id));
          analytics.capture('chat_conversation_starters_update', {
            tenantId,
            userId,
            previous,
            updated,
            added: updated.filter((s) => !prevIds.has(s.id)),
            removed: previous.filter((s) => !newIds.has(s.id)),
            modified: updated.filter((s) => {
              const old = previous.find((p) => p.id === s.id);
              return (
                old &&
                (old.title !== s.title ||
                  old.description !== s.description ||
                  old.content !== s.content)
              );
            }),
          });
        }
      },
      onError: () => toast.error('Failed to update conversation starters'),
    },
  );

  const [updateFiles] = useMutation(updateFilesMutation, {
    onCompleted: (data) => {
      if (data.updateTenantUserChatSettingsByPk) {
        const previous = files ?? [];
        const updated =
          (data.updateTenantUserChatSettingsByPk?.files as UploadedFile[]) ??
          [];
        const prevIds = new Set(previous.map((f) => f.id));
        const newIds = new Set(updated.map((f) => f.id));

        analytics.capture('chat_files_update', {
          tenantId,
          userId,
          previous,
          updated,
          added: updated.filter((f) => !prevIds.has(f.id)),
          removed: previous.filter((f) => !newIds.has(f.id)),
        });
      }
    },
    onError: () => toast.error('Failed to update files'),
  });

  const [deleteSettings] = useMutation(deleteChatSettingsMutation, {
    update: () => {
      cacheUtils.evict({
        key: { tenantId, userId },
        typeName: 'TenantUserChatSettings',
      });
    },
    onCompleted: (data) => {
      if (data.deleteTenantUserChatSettingsByPk) {
        analytics.capture('chat_settings_reset', {
          tenantId,
          userId,
        });
      }
    },
    onError: () => toast.error('Failed to reset settings'),
  });

  const handleResetSettings = async () => {
    try {
      setIsLoading(true);
      await deleteSettings({
        variables: { tenantId, userId },
      });

      setShowResetDialog(false);
      toast.success('Settings reset to defaults');
    } catch {
      toast.error('Failed to reset settings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveInstructions = async (value: string) => {
    if (settings) {
      await updateCustomInstructions({
        variables: { tenantId, userId, customInstructions: value },
      });
    } else {
      await upsertSettings({
        variables: {
          customInstructions: value,
        },
      });
    }
  };

  const handleSaveContextSources = async (sources: string[]) => {
    if (settings) {
      await updateContextSources({
        variables: { tenantId, userId, contextSources: sources },
      });
    } else {
      await upsertSettings({
        variables: {
          contextSources: sources,
        },
      });
    }
  };

  const handleSaveConversationStarters = async (
    starters: ConversationStarter[],
  ) => {
    if (settings) {
      await updateConversationStarters({
        variables: { tenantId, userId, conversationStarters: starters },
      });
    } else {
      await upsertSettings({
        variables: {
          conversationStarters: starters,
        },
      });
    }
  };

  const handleSaveFiles = async (uploadedFiles: UploadedFile[]) => {
    if (settings) {
      await updateFiles({
        variables: { tenantId, userId, files: uploadedFiles },
      });
    } else {
      await upsertSettings({
        variables: {
          files: uploadedFiles,
        },
      });
    }
  };

  return (
    <>
      <Helmet>
        <title>Chat Settings | Eluve</title>
      </Helmet>
      <Header>
        <SidebarTrigger />
        <HStack justify="end">
          <PageTitle isFullWidth>Chat Settings</PageTitle>
          <NewButton
            text="Reset to Default"
            type="outline"
            size="s"
            icon={{ name: 'RotateCcw', position: 'left' }}
            onClick={() => setShowResetDialog(true)}
          />
        </HStack>
      </Header>
      <Content padded>
        {isLoading ? (
          <LoadingIndicator />
        ) : (
          <VStack gap={6} className="w-full py-4">
            <CustomChatInstructions
              initialValue={customInstructions}
              onSave={handleSaveInstructions}
            />

            <ConversationStarters
              initialStarters={conversationStarters}
              onSave={handleSaveConversationStarters}
            />

            <KnowledgeSources
              tenantId={tenantId}
              initialSources={contextSources}
              initialFiles={files}
              onSave={handleSaveContextSources}
              onSaveFiles={handleSaveFiles}
            />
          </VStack>
        )}
      </Content>

      <AlertDialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset to Default Settings?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete all your custom chat settings including custom
              instructions, conversation starters, and uploaded files. The chat
              will revert to default settings. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleResetSettings}>
              Reset
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
